import React, { FC, useState, useRef, useEffect } from 'react';
import { Swiper as Carousel, SwiperSlide } from 'swiper/react/swiper-react.js';
import Swiper from 'swiper';
import { debounce } from 'lodash';
import { TalentCard } from '@src/containers/Source/TalentCard';
import { SignalTypeEnum, SignalEntityTypeEnum } from '@src/graphql/generated';
import { Skeleton } from '@src/containers/Source/TalentCard/Skeleton';
import { trackEvent } from '@src/metrics';
import { MetricAction } from '@src/metrics/metrics.enum';
import { recordSignals } from '@src/graphql/signals/actions';
import { ConnectParams } from '../Source/TalentCard/interfaces/connectParams.interface';
import { LastSlide } from './LastSlide';
import { ApplicantModalProps } from './props';
import './styles.scss';

export const ApplicantModal: FC<ApplicantModalProps> = ({
  activeUserId,
  feed,
  fetchMore,
  isLoading,
  hasMoreResults,
  isDirectConnectShownDefault,
  onConfirmTalent,
  customPitchMessage,
  onCloseModal,
  onDiscard,
  isFeaturedApplicantModal,
  openingState,
  openingId,
  isInitialLoading,
  invitedUserIds: providedInvitedUserIds,
  areVerificationStagesHidden,
  metricContext,
  onSlideChange,
}) => {
  const confirmMessageTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();

  const [invitedUserIds, setInvitedUserIds] = useState<string[]>(providedInvitedUserIds || []);

  const [swiperInstance, setSwiperInstance] = useState<Swiper>();

  const [isKeyboardDisable, setIsKeyboardDisable] = useState(!!isDirectConnectShownDefault);

  const initialActiveIndex = feed ? feed.findIndex(applicant => applicant.user.id === activeUserId) : 0;

  const swiperActiveIndex = useRef(0);

  const goNext = (userId?: string): void => {
    swiperActiveIndex.current += 1;

    if (swiperActiveIndex.current >= feed.length - 2) {
      fetchMore();
    }

    if (swiperInstance) {
      trackEvent({
        context: metricContext,
        interaction: MetricAction.VIEW_FEATURED_APPLICANT_MODAL,
        payload: {
          'User Id': userId || 'Reached the end',
        },
      });

      swiperInstance.slideNext();
    }
  };

  const goBack = (userId: string): void => {
    if (swiperActiveIndex.current > 0 && swiperInstance) {
      swiperActiveIndex.current -= 1;

      trackEvent({
        context: metricContext,
        interaction: MetricAction.VIEW_FEATURED_APPLICANT_MODAL,
        payload: {
          'User Id': userId,
        },
      });

      swiperInstance.slidePrev();
    }
  };

  useEffect(() => {
    const onKeyDown = debounce(event => {
      const code = event.code;

      const activeUserId = feed[swiperActiveIndex.current]?.user.id;

      switch (code) {
        case 'ArrowLeft':
        case 'Left':
          goBack(activeUserId);
          break;
        case 'ArrowRight':
        case 'Right':
          goNext(activeUserId);
          break;
      }
    }, 200);

    if (swiperInstance && !isKeyboardDisable) {
      document.addEventListener('keydown', onKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [swiperInstance, isKeyboardDisable, goBack, goNext, fetchMore]);

  const handleSlideChange = (index: number): void => {
    const userItem = feed[index];
    const userVerificationId = userItem?.userVerification?.id;

    if (userVerificationId) {
      recordSignals({
        items: [
          {
            id: userVerificationId,
            type: SignalEntityTypeEnum.USER_VERIFICATION,
          },
        ],
        signalType: SignalTypeEnum.VIEW_USER_VERIFICATION,
      });
    }

    onSlideChange?.(userItem);
  };

  const handleConfirmClick = (userId: string, params?: ConnectParams): void => {
    setInvitedUserIds(prevUserIds => [...prevUserIds, userId]);

    onConfirmTalent(userId, params);
  };

  useEffect(() => {
    if (!isInitialLoading && swiperInstance) {
      swiperActiveIndex.current = initialActiveIndex;
      swiperInstance.slideTo(initialActiveIndex, 0);
    }
  }, [isInitialLoading, swiperInstance]);

  useEffect(() => {
    trackEvent({
      context: metricContext,
      interaction: MetricAction.VIEW_FEATURED_APPLICANT_MODAL,
      payload: {
        'User Id': activeUserId,
      },
    });

    return () => {
      if (confirmMessageTimeout.current) {
        clearTimeout(confirmMessageTimeout.current);
      }
    };
  }, []);

  return (
    <Carousel
      allowTouchMove={false}
      centeredSlides={false}
      loop={false}
      onSlideChange={swiper => {
        swiper.update();

        handleSlideChange(swiper.activeIndex);
      }}
      onSwiper={swiper => {
        if (!swiperInstance) {
          setSwiperInstance(swiper);
        }
      }}
      slidesPerView={'auto'}
      spaceBetween={16}
    >
      {feed.map((applicant, index) => {
        return (
          <SwiperSlide key={applicant.userVerification.id}>
            {({ isActive }) => {
              return index + 3 > swiperActiveIndex.current && index - 3 < swiperActiveIndex.current ? (
                <TalentCard
                  areVerificationStagesHidden={areVerificationStagesHidden}
                  customPitchMessage={customPitchMessage}
                  goBack={() => goBack(applicant.user.id)}
                  goNext={() => goNext(applicant.user.id)}
                  isDirectConnectShownDefault={isDirectConnectShownDefault && initialActiveIndex === index}
                  isFeaturedApplicantModal={isFeaturedApplicantModal}
                  isFooterVisible={true}
                  isHideActionVisible={true}
                  isInvited={!!invitedUserIds.includes(applicant.user.id)}
                  isKeyboardDisable={isKeyboardDisable}
                  isVisible={!!isActive}
                  onConfirmTalent={params => handleConfirmClick(applicant.user.id, params)}
                  onDiscardTalent={onDiscard}
                  openingId={openingId}
                  openingState={openingState}
                  setIsKeyboardDisable={setIsKeyboardDisable}
                  userId={applicant.user.id}
                  userPartialData={applicant}
                  wrapperClassName="ta-modal-carousel-card"
                />
              ) : null;
            }}
          </SwiperSlide>
        );
      })}

      {!isLoading && !hasMoreResults ? (
        <SwiperSlide key="ta-carousel-card-skeleton">
          <LastSlide onClick={onCloseModal} />
        </SwiperSlide>
      ) : null}

      {isLoading ? (
        <SwiperSlide key="ta-carousel-card-skeleton">
          <Skeleton />
        </SwiperSlide>
      ) : null}
    </Carousel>
  );
};
